import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { HttpAFOPU } from '../api/httoAFOPU';
import { OperacionAFOPUTablaValorVista, OperacionAFOPUTablaValorVistaInicial } from '../types/dto';
import { OperacionAFOPUTablaValorFields } from '../types/fields';

export interface AFOPURow {
  codInstrumento: number;
  descInstrumento: string;
  ordenInstrumento: number;
  celdas: Record<number, OperacionAFOPUTablaValorVistaInicial>;
}

export interface AFOPUHeader {
  codOperacionTipo: number;
  descOperacionTipo: string;
  ordenOperacionTipo: number;
}


export const pivotAFOPUData = (flatData: any[]) => {
  if (!flatData || !flatData.length) return { headers: [], rows: [] };

  const headersMap = new Map<number, AFOPUHeader>();
  const rowsMap = new Map<number, AFOPURow>();

  flatData.forEach((item) => {
    const codTipo = item[OperacionAFOPUTablaValorFields.CodOperacionTipo];
    const codInst = item[OperacionAFOPUTablaValorFields.CodInstrumento];

    if (!headersMap.has(codTipo)) {
      headersMap.set(codTipo, {
        codOperacionTipo: codTipo,
        descOperacionTipo: item[OperacionAFOPUTablaValorFields.DescOperacionTipo],
        ordenOperacionTipo: item[OperacionAFOPUTablaValorFields.OrdenOperacionTipo] ?? 0
      });
    }

    if (!rowsMap.has(codInst)) {
      rowsMap.set(codInst, {
        codInstrumento: codInst,
        descInstrumento: item[OperacionAFOPUTablaValorFields.DescInstrumento],
        ordenInstrumento: item[OperacionAFOPUTablaValorFields.OrdenInstrumento] ?? 0,
        celdas: {}
      });
    }

    const row = rowsMap.get(codInst)!;
    row.celdas[codTipo] = item;
  });

  const headers = Array.from(headersMap.values()).sort((a, b) => a.ordenOperacionTipo - b.ordenOperacionTipo);
  const rows = Array.from(rowsMap.values()).sort((a, b) => a.ordenInstrumento - b.ordenInstrumento);

  return { headers, rows };
};

// pivot de los datos originales
export const useAFOPUTablaValorInicial = (codPais: number | undefined | null) => {
  const {
    data: tablaValorOriginal = [],
    isLoading,
    isError,
    error
  } = useQuery<OperacionAFOPUTablaValorVistaInicial[], Error>({
    queryKey: ['AFOPU-tabla-valor-inicial', codPais],
    queryFn: () => HttpAFOPU.getTablaValoresInicial(codPais!),
    enabled: !!codPais
  });

  const pivotData = useMemo(() => pivotAFOPUData(tablaValorOriginal), [tablaValorOriginal]);

  return {
    headers: pivotData.headers,
    rows: pivotData.rows,
    aforos: tablaValorOriginal,
    isLoading,
    isError,
    error
  };
};


export const useAFOPUTablaValor = (idOperacion: number | undefined | null) => {
  const {
    data: tablaValorData = [],
    isLoading,
    isError,
    error,
    refetch
  } = useQuery<OperacionAFOPUTablaValorVista[], Error>({
    queryKey: ['AFOPU-tabla-valor', idOperacion],
    queryFn: () => HttpAFOPU.getTablaValores(idOperacion!),
    enabled: !!idOperacion
  });

  const pivotData = useMemo(() => pivotAFOPUData(tablaValorData), [tablaValorData]);

  return {
    headers: pivotData.headers,
    rows: pivotData.rows,
    aforos: tablaValorData,
    isLoading,
    isError,
    error,
    refetch
  };
};

